/**
 * Бизнес-логика корзины: подсчёт цены позиции, сравнение кондиментов/модификаторов.
 * Здесь нет API-вызовов — только обработка данных корзины.
 */
import type { Condiment, ModifierGroup } from '@/types'

interface SelectedModItem {
  id: string | number
  quantity?: number
}

interface SelectedModGroup {
  id: string | number
  items?: SelectedModItem[]
}

interface PriceableItem {
  mods?: SelectedModGroup[]
  group_mods?: ModifierGroup[]
  active_condiments?: Condiment[]
  condiments?: Condiment[]
  comboID?: string | number | null
  min_price_with_group_mods?: number | null
  price?: number
  quantity?: number
}

// Единая функция расчёта полной стоимости позиции корзины
// (базовая цена + выбранные модификаторы + кондименты) * количество.
// Использует реальные цены модификаторов (sourceItem.price), а не display_price.
export function getItemTotalPrice(item: PriceableItem | null | undefined): number {
    if (!item) { return 0; }
    const qty = Number(item.quantity) || 1;

    let modPrice = 0;
    if (Array.isArray(item.mods) && item.mods.length && Array.isArray(item.group_mods)) {
        for (const group of item.mods) {
            if (!group || !Array.isArray(group.items)) { continue; }
            for (const selected of group.items) {
                const sourceGroup = item.group_mods.find(g => g.id === group.id);
                if (sourceGroup && Array.isArray(sourceGroup.items)) {
                    const sourceItem = sourceGroup.items.find(i => i.id === selected.id);
                    if (sourceItem && typeof sourceItem.price === 'number') {
                        modPrice += sourceItem.price * (selected.quantity || 1);
                    }
                }
            }
        }
    }

    let condimentsPrice = 0;
    if (Array.isArray(item.active_condiments) && Array.isArray(item.condiments)) {
        condimentsPrice = item.active_condiments.reduce((sum, activeCondiment) => {
            const cond = item.condiments!.find(c => c.id === activeCondiment.id);
            return (cond && cond.price) ? sum + cond.price : sum;
        }, 0);
    }

    const hasGroupModsStructure = Array.isArray(item.group_mods) && item.group_mods.length;
    let basePrice: number;
    if (!hasGroupModsStructure && item.comboID == null && item.min_price_with_group_mods != null) {
        // На товаре нет структуры group_mods — берём готовую минимальную цену с модификаторами
        basePrice = Number(item.min_price_with_group_mods || 0);
    } else {
        basePrice = Number(item.price || 0);
    }

    return (basePrice + modPrice + condimentsPrice) * qty;
}

export function activeCondimentsEqual(a: Condiment[] | undefined | null, b: Condiment[] | undefined | null): boolean {
    if (!Array.isArray(a) && !Array.isArray(b)) { return true; }
    if (!Array.isArray(a) || !Array.isArray(b)) { return false; }
    if (a.length !== b.length) { return false; }
    const idsA = a.map(x => x.id).sort();
    const idsB = b.map(x => x.id).sort();
    return idsA.every((id, idx) => id === idsB[idx]);
}

export function modsEqual(a: ModifierGroup[] | undefined | null, b: ModifierGroup[] | undefined | null): boolean {
    if (!Array.isArray(a) && !Array.isArray(b)) { return true; }
    if (!Array.isArray(a) || !Array.isArray(b)) { return false; }
    if (a.length !== b.length) { return false; }

    return a.every(groupA => {
        const groupB = b.find(g => g.id === groupA.id);
        if (!groupB) { return false; }

        if (!Array.isArray(groupA.items) && !Array.isArray(groupB.items)) { return true; }
        if (!Array.isArray(groupA.items) || !Array.isArray(groupB.items)) { return false; }
        if (groupA.items.length !== groupB.items.length) { return false; }

        const idsA = groupA.items.map(x => x.id).sort();
        const idsB = groupB.items.map(x => x.id).sort();
        return idsA.every((id, idx) => id === idsB[idx]);
    });
}